import React, {useMemo, useState, useCallback} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';
import Moment from 'moment';
import {INITIALS_SIZE, MODULE_TYPES} from '../../../constants';
import TdLink from '../../../components/td_link';
import Dropdown from '../../shared/components/dropdowns/dropdown';
import Util from '../../shared/util/util';
import UpdatePersonMutation from '../../../mutations/update_person_mutation.modern';
import {createToast} from '../../shared/components/toasts/another-toast/toaster';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import HarvestIcon from '../../../images/integrations/harvest-logo.png';
import GitLabIcon from '../../../images/integrations/gitlab.png';
import JiraIcon from '../../../images/integrations/jira-logo.png';
import AsanaIcon from '../../../images/integrations/asana-logo.png';
import Unit4Icon from '../../../images/integrations/unit4-small-logo.svg';
import HiBobLogo from '../../../images/integrations/hibob/HiBob-logo.png';
import {
	AdoIcon,
	BambooHRIcon,
	Checkbox,
	EconomicIcon,
	GithubIcon,
	MSTeamsIcon,
	SageIntacctIcon,
	SlackIcon,
} from '@/web-components';
import PersonInitials from '../../shared/components/person/person_initials';
import {
	personHasPermission,
	personIsAdmin,
	personHasContributerProfile,
	personIsVirtualUser,
} from '../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {hasModule} from '../../shared/util/ModuleUtil';
import {useSettingsPeopleContext} from './SettingsPeopleContext';
import {StartDate, EndDate} from './PeoplePersonDateCells';
import hash from 'object-hash';
import ActionsMenu from '../../shared/components/action-menu/actions_menu';
import {usesSso} from '../../shared/util/FeatureUtil';
import {profilePicSrc} from '../../../directApi';

const NameInfo = React.memo(
	({person, linkPath}) => (
		<TdLink
			cy="name-width"
			className="person person-width"
			path={linkPath}
			content={
				<div className="wrapper" data-cy="name-width">
					<div className="image-container">
						<div className="profile-picture">
							{person.profilePictureId ? (
								<img
									crossOrigin="use-credentials"
									data-private
									src={profilePicSrc(person.profilePictureId)}
									width="36"
									height="36"
									alt=""
								/>
							) : (
								<PersonInitials initials={person.initials} size={INITIALS_SIZE.SMALL} />
							)}
						</div>
					</div>
					<div className="name-tile">
						<h3 className="name">
							{(person.firstName || '') + (person.lastName !== null ? ' ' + person.lastName : '')}
						</h3>
					</div>
				</div>
			}
		/>
	),
	(p1, p2) => p1.person?.id === p2.person?.id
);

const Role = React.memo(
	({person, roles, onRoleChange}) => (
		<td className="role-width">
			<Dropdown
				inputCy={'role-dropdown'}
				focusOnClick={true}
				optionClickEvent={true}
				onChange={onRoleChange}
				options={roles.map(role => {
					return {label: role.node.name, value: role.node.id};
				})}
				value={person.role ? person.role.id : ''}
				hideLabel={true}
				disabled={false}
				listDataCy="role"
			/>
		</td>
	),
	(p1, p2) => p1.person?.role?.id === p2.person?.role?.id
);

const Skills = React.memo(
	({person, formatMessage, showPersonSkillsModal}) =>
		Util.hasSkills() && (
			<td
				style={{textTransform: 'lowercase'}}
				onClick={showPersonSkillsModal}
				className={'person-skill-link '}
				data-cy={'person-skill'}
			>
				{formatMessage({id: 'settings_skills.skills_count'}, {count: person.skillPersons?.length || 0})}
			</td>
		),
	(p1, p2) => p1.person.skillPersons?.length === p2.person.skillPersons?.length
);

const Department = React.memo(
	({person, departments, onDepartmentChange}) => (
		<td className="department-width">
			<Dropdown
				inputCy={'department-dropdown'}
				focusOnClick={true}
				optionClickEvent={true}
				onChange={onDepartmentChange}
				options={departments.map(department => {
					return {label: department.node.name, value: department.node.id};
				})}
				value={person.department ? person.department.id : ''}
				hideLabel={true}
				disabled={false}
				listDataCy="department"
			/>
		</td>
	),
	(p1, p2) => p1.person?.department?.id === p2.person?.department?.id
);

const Profiles = React.memo(
	({person, viewer, profiles, onProfileChange, disabled}) => (
		<td className="permissions-width">
			<Dropdown
				multiSelect={hasModule(MODULE_TYPES.SISENSE)}
				hideLabel={true}
				onChange={onProfileChange}
				options={profiles.map(profile => {
					if (
						viewer.actualPersonId === person.id &&
						(personIsAdmin(profile.profile) || personIsVirtualUser(profile.profile))
					) {
						return {...profile, disabled: true};
					}
					return profile;
				})}
				value={
					hasModule(MODULE_TYPES.SISENSE)
						? person.profiles.edges.map(e => e.node.id)
						: person.profiles.edges[0]?.node.id
				}
				disabled={(viewer.actualPersonId === person.id && !hasModule(MODULE_TYPES.SISENSE)) || disabled}
			/>
		</td>
	),
	(p1, p2) =>
		hash(p1.person?.profiles) === hash(p2.person?.profiles) &&
		hash(p1.disabled) === hash(p2.disabled) &&
		p1.person?.seat === p2.person?.seat
);

const Cost = React.memo(
	({currentCostPeriod, currencySymbol, showInternalCostPeriodsModal}) => {
		const intl = useIntl();

		return (
			<td onClick={showInternalCostPeriodsModal} className={'cost cost-modal-link'}>
				{Util.getFormattedNumberWithCurrency(currencySymbol, currentCostPeriod ? currentCostPeriod.node.cost : 0, intl)}
			</td>
		);
	},
	(p1, p2) => p1.currentCostPeriod?.node?.cost === p2.currentCostPeriod?.node?.cost
);

const CreatedAt = React.memo(
	({createdAtMoment}) => (
		<td className="created-at-cell" title={createdAtMoment.format('LLL')}>
			{createdAtMoment.format('LL')}
		</td>
	),
	(p1, p2) => p1.createdAtMoment === p2.createdAtMoment
);

const CreatedBy = React.memo(
	({createdByPerson}) => <td className="created-by-cell">{createdByPerson ? createdByPerson.node.fullName : null}</td>,
	(p1, p2) => p1.createdByPerson === p2.createdByPerson
);

const Integrations = React.memo(
	({person, viewer, linkPath}) => (
		<td className="integrations-cell">
			<div style={{display: 'flex', gap: '4px'}}>
				{viewer.company.harvestEnabled && person.harvestUser && (
					<div className="harvest-icon">
						<img
							width={20}
							height={20}
							src={HarvestIcon}
							alt="Harvest"
							title="Harvest"
							onClick={e => {
								e.stopPropagation();
								window.open(linkPath, '_self');
							}}
						/>
					</div>
				)}
				{person.economicUser && <EconomicIcon />}
				{person.gitlabUser && person.gitlabUser.integrationUserId && (
					<img width={20} height={20} src={GitLabIcon} alt="GitLab" title="GitLab" />
				)}
				{person.gitlabServerUser && person.gitlabServerUser.integrationUserId && (
					<img width={20} height={20} src={GitLabIcon} alt="GitLab Server" title="GitLab Server" />
				)}
				{person.githubUser && person.githubUser.integrationUserId && <GithubIcon />}
				{person.jiraServerUser && <img width={20} height={20} src={JiraIcon} alt="Jira Server" title="Jira Server" />}
				{person.jiraCloudUser && <img width={20} height={20} src={JiraIcon} alt="Jira Cloud" title="Jira Cloud" />}
				{person.asanaUser && <img width={20} height={20} src={AsanaIcon} alt="Asana" title="Asana" />}
				{person.adoUserId && <AdoIcon />}
				{person.unit4User && <img width={20} height={20} src={Unit4Icon} alt="Unit4" title="Unit4" />}
				{person.msTeamsId && <MSTeamsIcon />}
				{person.bambooHRId && <BambooHRIcon />}
				{person.slackId && <SlackIcon />}
				{person.sageIntacctId && <SageIntacctIcon />}
				{person.hiBobId && <img width={28} height={28} src={HiBobLogo} alt="HiBob" title="HiBob" />}
			</div>
		</td>
	),
	(p1, p2) => p1.person?.id === p2.person?.id
);

const SeatType = React.memo(
	({person, getSeatTypeName}) => (
		<td data-cy={'person-seat'} className={'person-seat'}>
			{getSeatTypeName(person?.seat)}
		</td>
	),
	(p1, p2) => p1.person?.id === p2.person?.id && p1.person?.seat === p2.person?.seat
);

const Status = React.memo(
	({person, getPersonStatus}) => (
		<td data-cy={'person-status'} className={'person-status'}>
			{getPersonStatus(person)}
		</td>
	),
	(p1, p2) => p1.person?.id === p2.person?.id
);

export const PeoplePerson = ({
	firstUser,
	hasFinancial,
	person,
	viewer,
	integrationsEnabled,
	theEyeOptions,
	roles,
	departments,
	profiles,
}) => {
	const {formatMessage} = useIntl();

	const {isSelectedPerson, toggleSelectedPerson, getPersonStatus, getSeatTypeName, getActionOptions, isCurrentUser} =
		useSettingsPeopleContext();
	const {company} = viewer;

	const [isProfileUpdating, setIsProfileUpdating] = useState(false);

	const showChangesSavedToast = useCallback(
		duration => {
			createToast({duration, message: formatMessage({id: 'common.changes_saved'})});
		},
		[formatMessage]
	);

	const onRoleChange = role => {
		if (!person || !role) return;

		// Prevent if the role is the same
		if (person.role && person.role.id === role.value) return;

		Util.CommitMutation(
			UpdatePersonMutation,
			{
				id: person.id,
				roleId: role.value,
			},
			() => showChangesSavedToast(2000),
			true
		);
	};

	const onDepartmentChange = department => {
		if (!person || !department) return;

		// Prevent if the role is the same
		if (person.department && person.department.id === department.value) return;

		Util.CommitMutation(
			UpdatePersonMutation,
			{
				id: person.id,
				departmentId: department.value,
			},
			() => showChangesSavedToast(2000),
			true
		);
	};

	const runProfileMutation = profile => {
		const data = {
			id: person.id,
		};
		if (!person.invited && personIsVirtualUser(person) && !personIsVirtualUser(profile.profile)) {
			data.invited = true;
		}
		const onSuccess = res => {
			setIsProfileUpdating(false);
			showChangesSavedToast(2000);
			const errors = res.updatePerson?.errors || [];
			if (errors.includes('NO_DESIGNER_SEATS_LEFT') || errors.includes('NO_VIEWER_SEATS_LEFT')) {
				showModal({
					type: MODAL_TYPE.WARNING,
					message: 'Not enough Advanced Analytics seats',
					warningInformation: ['Please contact your account manager if you need to increase your seat limit.'],
				});
			} else if (errors.includes('NO_AVAILABLE_SEATS')) {
				showModal({
					type: MODAL_TYPE.NO_AVAILABLE_SEATS,
					seats: company.userSeats,
					seatType: company.tier === 'CORE' ? 'core ' : 'full ',
					warningMessageId: 'team_modal.seats_warning_permission_change_failed',
				});
			} else if (errors.includes('NO_AVAILABLE_VIRTUAL_SEATS')) {
				showModal({
					type: MODAL_TYPE.NO_AVAILABLE_SEATS,
					seats: company.virtualSeats,
					seatType: 'virtual ',
					warningMessageId: 'team_modal.seats_warning_permission_change_failed',
				});
			} else if (errors.includes('NO_AVAILABLE_COLLABORATOR_SEATS')) {
				showModal({
					type: MODAL_TYPE.NO_AVAILABLE_SEATS,
					seats: company.collaboratorSeats,
					seatType: 'collaborator ',
					warningMessageId: 'team_modal.seats_warning_permission_change_failed',
				});
			}
		};
		const profileRealId = +atob(profile.profile.id).replace('Profile:', '');
		if (!personIsVirtualUser(person) && profileRealId === -1) {
			// Changing to virtual
			data.profileIds = [profile.profile.id];
		} else if (personIsVirtualUser(person) && profileRealId !== -1) {
			// Changing from virtual
			data.profileIds = [profile.profile.id];
		} else if (!personHasContributerProfile(person) && profileRealId === -4) {
			// Changing to Collaborator seat / Contributer profile
			data.profileIds = [profile.profile.id];
		} else if (personHasContributerProfile(person) && profileRealId !== -4) {
			// Changing from Collaborator seat / Contributer profile
			data.profileIds = [profile.profile.id];
		} else if (!hasModule(MODULE_TYPES.SISENSE)) {
			// Use only single select. So just set the profile id
			data.profileIds = [profile.profile.id];
		} else if (person.profiles.edges.some(e => e.node.id === profile.profile.id)) {
			// Person already has that profile. So remove it.
			// If person only has one profile, that cannot be removed.
			if (person.profiles.edges.length === 1) return;
			data.profileIds = person.profiles.edges.filter(e => e.node.id !== profile.profile.id).map(e => e.node.id);
		} else {
			// Person does not have the profile.
			data.profileIds = [...person.profiles.edges.map(e => e.node.id), profile.profile.id];
		}

		if (
			personHasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS, profile.profile) &&
			!person.profiles.edges.some(e => e.node.id === profile.profile.id) &&
			!usesSso(company)
		) {
			showModal({
				type: MODAL_TYPE.CONFIRM_PASSWORD_MODAL,
				mutation: UpdatePersonMutation,
				mutationName: 'updatePerson',
				data,
				onSuccess,
			});
		} else {
			setIsProfileUpdating(true);
			Util.CommitMutation(UpdatePersonMutation, data, onSuccess);
		}
	};

	const onProfileChange = profile => {
		if (!person || !profile) return;

		if (personIsVirtualUser(person) && !person.email) {
			document.activeElement.blur();
			showModal({
				type: MODAL_TYPE.ADD_EMAIL,
				personId: person.id,
				callback: () => runProfileMutation(profile),
			});
		} else {
			runProfileMutation(profile);
		}
	};

	const updateStartDate = useCallback(
		startDate => {
			const onSuccess = () => {
				showChangesSavedToast(2000);
			};
			Util.CommitMutation(
				UpdatePersonMutation,
				{
					id: person.id,
					startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
				},
				onSuccess
			);
		},
		[person, showChangesSavedToast]
	);

	const updateEndDate = useCallback(
		endDate => {
			const onSuccess = () => {
				showChangesSavedToast(2000);
			};
			Util.CommitMutation(
				UpdatePersonMutation,
				{
					id: person.id,
					endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
				},
				onSuccess
			);
		},
		[person, showChangesSavedToast]
	);

	const linkPath = useMemo(
		() => (person.isViewer ? '/my-profile/account-configuration' : '/admin/team/view-user/' + person.id),
		[person.id, person.isViewer]
	);

	const showInternalCostPeriodsModal = () => {
		showModal({
			type: MODAL_TYPE.COST_PERIODS,
			personId: person.id,
		});
	};

	const showPersonSkillsModal = () => {
		showModal({
			type: MODAL_TYPE.PERSON_SKILLS,
			personId: person.id,
		});
	};

	const currencySymbol = Util.GetCurrencySymbol(company.currency);
	const currentCostPeriod = Util.getCurrentCostPeriod(person.costPeriods.edges);

	const createdByPerson = company.allPersons.edges.find(p => p.node.id === person.createdBy);
	const createdAtMoment = Moment(person.createdAt);

	const PersonSelector = () => (
		<td className={'selection'}>
			<Checkbox
				disabled={isCurrentUser(person)}
				checked={isSelectedPerson(person.id)}
				onClick={() => toggleSelectedPerson(person.id)}
				cy={'selector-checkbox'}
				userpilot={'bulk-update-checkbox'}
			/>
		</td>
	);

	const Actions = () => (
		<td data-cy={'person-actions'} className={'action-links'}>
			<ActionsMenu
				disabled={isCurrentUser(person)}
				customWidth={20}
				whiteInner={true}
				isWhite={false}
				options={getActionOptions(person)}
				leftOffset={-10}
			/>
		</td>
	);

	return (
		<tr
			data-cy="row-member"
			key={'row' + person.id}
			className="row-member person-row"
			data-userpilot={firstUser ? 'person-first' : null}
		>
			<PersonSelector />
			<NameInfo person={person} linkPath={linkPath} />
			<Role person={person} roles={roles} onRoleChange={onRoleChange} />
			{Util.hasSkills() && (
				<Skills person={person} formatMessage={formatMessage} showPersonSkillsModal={showPersonSkillsModal} />
			)}
			{Util.hasDepartments() ? (
				<Department person={person} departments={departments} onDepartmentChange={onDepartmentChange} />
			) : null}
			<Profiles
				person={person}
				viewer={viewer}
				profiles={profiles}
				onProfileChange={profile => onProfileChange(profile)}
				disabled={isProfileUpdating}
			/>
			{hasFinancial ? (
				<Cost
					currentCostPeriod={currentCostPeriod}
					currencySymbol={currencySymbol}
					showInternalCostPeriodsModal={showInternalCostPeriodsModal}
				/>
			) : null}
			<StartDate person={person} onDateChange={updateStartDate} />
			<EndDate person={person} onDateChange={updateEndDate} />
			{theEyeOptions && theEyeOptions.find(option => option.name === 'created-at').checked ? (
				<CreatedAt createdAtMoment={createdAtMoment} />
			) : null}
			{theEyeOptions && theEyeOptions.find(option => option.name === 'created-by').checked ? (
				<CreatedBy createdByPerson={createdByPerson} />
			) : null}
			{integrationsEnabled ? <Integrations person={person} viewer={viewer} linkPath={linkPath} /> : null}
			<SeatType person={person} getSeatTypeName={getSeatTypeName} />
			<Status person={person} getPersonStatus={getPersonStatus} />
			<Actions />
		</tr>
	);
};
